import { getApiUrl } from "src/env";
import type {
  ConsentType,
  InsuranceCardSide,
  InviteResolution,
  JoinedQueueEntry,
  RequiredConsents,
  Submissions,
} from "src/api/types";

const API_URL = getApiUrl();

async function parseError(res: Response): Promise<string> {
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  return body.error ?? "unknown_error";
}

export async function exchangeStreamToken(
  clientId: string,
  streamToken: string,
): Promise<{ sse_token: string }> {
  const res = await fetch(`${API_URL}/stream/exchange`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ client_id: clientId, stream_token: streamToken }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? "exchange_failed");
  }
  return res.json() as Promise<{ sse_token: string }>;
}

export function buildStreamURL(sseToken: string): string {
  return `${API_URL}/stream?token=${encodeURIComponent(sseToken)}`;
}

export async function resolveInvite(shortCode: string): Promise<InviteResolution> {
  const res = await fetch(`${API_URL}/invites/${shortCode}`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<InviteResolution>;
}

export async function joinInvite(shortCode: string, retryKey: string): Promise<JoinedQueueEntry> {
  const res = await fetch(`${API_URL}/invites/${shortCode}/join`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ retry_key: retryKey }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<JoinedQueueEntry>;
}

export async function uploadInsuranceCardImages(
  clientId: string,
  streamToken: string,
  front: Blob,
  back: Blob,
): Promise<void> {
  const form = new FormData();
  form.append("client_id", clientId);
  form.append("stream_token", streamToken);
  form.append("frontImage", front, `front.${imageExtension(front)}`);
  form.append("backImage", back, `back.${imageExtension(back)}`);

  let res: Response;
  try {
    res = await fetch(`${API_URL}/insurance-card-images`, { method: "POST", body: form });
  } catch {
    throw new Error("network_error");
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; image?: string };
    const image = body.image === "front" || body.image === "back" ? body.image : undefined;
    throw new InsuranceCardUploadError(body.error ?? "unknown_error", image);
  }
}

// image is set when the backend says which photo was the problem.
export class InsuranceCardUploadError extends Error {
  image?: InsuranceCardSide;

  constructor(code: string, image?: InsuranceCardSide) {
    super(code);
    this.image = image;
  }
}

function imageExtension(blob: Blob): string {
  return blob.type === "image/png" ? "png" : "jpg";
}

export async function getSubmissions(clientId: string, streamToken: string): Promise<Submissions> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}/submissions?${credentialQuery(clientId, streamToken)}`);
  } catch {
    throw new Error("network_error");
  }
  if (!res.ok) throw new Error(await parseError(res));
  const body = (await res.json()) as {
    insurance_card_submitted?: boolean;
    consents_submitted?: boolean;
  };
  return {
    insuranceCardSubmitted: body.insurance_card_submitted === true,
    consentsSubmitted: body.consents_submitted === true,
  };
}

export async function getRequiredConsents(
  clientId: string,
  streamToken: string,
): Promise<RequiredConsents> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}/consents/required?${credentialQuery(clientId, streamToken)}`);
  } catch {
    throw new Error("network_error");
  }
  if (!res.ok) throw new Error(await parseError(res));
  const body = (await res.json()) as { consentTypes?: ConsentType[]; state?: string };
  return { consentTypes: body.consentTypes ?? [], state: body.state || undefined };
}

// The PDF viewer fetches this URL itself.
export function consentPreviewUrl(
  clientId: string,
  streamToken: string,
  type: ConsentType,
): string {
  return `${API_URL}/consents/${type}/preview?${credentialQuery(clientId, streamToken)}`;
}

export async function submitConsents(
  clientId: string,
  streamToken: string,
  consentTypes: ConsentType[],
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${API_URL}/consents`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client_id: clientId, stream_token: streamToken, consentTypes }),
    });
  } catch {
    throw new Error("network_error");
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      consentTypes?: ConsentType[];
    };
    if (res.status === 409 && body.error === "consents_changed") {
      throw new ConsentsChangedError(body.consentTypes ?? []);
    }
    throw new Error(body.error ?? "unknown_error");
  }
}

// Thrown on 409 consents_changed; consentTypes is the current required list.
export class ConsentsChangedError extends Error {
  consentTypes: ConsentType[];

  constructor(consentTypes: ConsentType[]) {
    super("consents_changed");
    this.consentTypes = consentTypes;
  }
}

function credentialQuery(clientId: string, streamToken: string): string {
  return new URLSearchParams({ client_id: clientId, stream_token: streamToken }).toString();
}
